/**
 * Las obras sociales y prepagas que un negocio de salud puede declarar.
 *
 * Fuente única: la usan el panel (para que el consultorio tilde las que atiende)
 * y la página pública (para mostrarle al paciente sólo esas). Una lista escrita
 * dos veces es una que se desincroniza, y acá además romperia el vínculo con el
 * logo: el `id` es lo que se guarda en el negocio y en el turno, y lo que
 * después permite encontrar el archivo del logo.
 *
 * POR QUÉ HAY UNA LISTA MAESTRA Y NO UN CAMPO LIBRE
 *
 * Con texto libre no hay forma de saber que "OSDE", "osde" y "O.S.D.E." son la
 * misma, ni qué logo le corresponde a lo que alguien escribió. El negocio elige
 * de acá; si falta alguna, se agrega en este archivo y aparece para todos.
 *
 * SOBRE `color` Y `logo`
 *
 * Todavía no están: van a llegar con los archivos de marca de cada una. Hasta
 * entonces el botón se dibuja con el nombre sobre el color de la marca del
 * negocio, que es lo que hace el resto de la página. Cuando estén, el botón lee
 * `logo` y `color` de acá y no hay que tocar ningún componente:
 *
 *   { id: 'osde', nombre: 'OSDE', color: '#0067B1', logo: '/obras-sociales/osde.svg' }
 *
 * OJO con los logos: son marcas registradas de terceros. Mostrarlas para indicar
 * "acá te atendemos con esta cobertura" es uso nominativo y es lo normal en el
 * rubro, pero conviene servir los archivos desde `public/obras-sociales/` y no
 * hotlinkear el sitio de cada una.
 */

/**
 * El id de "sin cobertura". Vive como constante porque lo miran tres lugares
 * —el paso de la reserva, el resumen y el panel— y es el único que NO se elige
 * desde la lista del negocio: se ofrece siempre.
 */
export const PARTICULAR = 'particular';

export const obrasSociales = [
  { id: 'osde', nombre: 'OSDE' },
  { id: 'swiss-medical', nombre: 'Swiss Medical' },
  { id: 'galeno', nombre: 'Galeno' },
  { id: 'medife', nombre: 'Medifé' },
  { id: 'omint', nombre: 'OMINT' },
  { id: 'sancor-salud', nombre: 'Sancor Salud' },
  { id: 'medicus', nombre: 'Medicus' },
  { id: 'avalian', nombre: 'Avalian' },
  { id: 'premedic', nombre: 'Premedic' },
  { id: 'prevencion-salud', nombre: 'Prevención Salud' },
  { id: 'hospital-italiano', nombre: 'Plan de Salud Hospital Italiano' },
  { id: 'hospital-britanico', nombre: 'Hospital Británico' },
  { id: 'osecac', nombre: 'OSECAC' },
  { id: 'osde-binario', nombre: 'OSDE Binario' },
  { id: 'ioma', nombre: 'IOMA' },
  { id: 'pami', nombre: 'PAMI' },
  { id: 'osdepym', nombre: 'OSDEPYM' },
  { id: 'union-personal', nombre: 'Unión Personal' },
  { id: 'osperyh', nombre: 'OSPERYH' },
  { id: 'federada-salud', nombre: 'Federada Salud' },
  { id: 'jerarquicos', nombre: 'Jerárquicos Salud' },
  { id: 'luis-pasteur', nombre: 'Luis Pasteur' },
];

/** La de "particular", que no se elige desde el panel: se ofrece siempre. */
export const coberturaParticular = {
  id: PARTICULAR,
  nombre: 'Particular',
  descripcion: 'Sin cobertura',
};

const porId = new Map(obrasSociales.map((o) => [o.id, o]));

/**
 * De un id al objeto. Devuelve `null` si el id no está en la lista —puede pasar
 * con un turno viejo, o si alguna vez se saca una de acá—, así que quien lo use
 * tiene que contemplar el caso en vez de romper mostrando `undefined.nombre`.
 */
export const buscarObraSocial = (id) => {
  if (id === PARTICULAR) return coberturaParticular;
  return porId.get(id) || null;
};

/**
 * El nombre que se muestra. Para un id desconocido devuelve el id crudo en vez
 * de vacío: en el panel es preferible ver "obra-social-vieja" y entender que
 * hay algo raro, antes que una fila que parece no tener cobertura cargada.
 */
export const nombreDeObraSocial = (id, propias = []) => {
  if (!id) return '';
  const propia = (Array.isArray(propias) ? propias : []).find((o) => o?.id === id);
  return buscarObraSocial(id)?.nombre || propia?.nombre || id;
};

/**
 * El id que le corresponde a una cobertura escrita a mano.
 *
 * Es el nombre reducido a letras, números y guiones — la misma forma que ya
 * tienen los ids de la lista maestra y la única que acepta el backend. Sin
 * tildes ni mayúsculas por lo mismo que dice el comentario de arriba: es lo que
 * evita que "Osde Región" y "OSDE REGION" queden como dos coberturas distintas.
 */
export const idDeObraSocial = (nombre) =>
  String(nombre ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);

/**
 * Las que el negocio ofrece, en el orden de la lista maestra y no en el que las
 * tildó: así todos los consultorios muestran las mismas en el mismo lugar, y
 * quien reserva en dos no tiene que buscar de nuevo.
 *
 * `propias` son las que el negocio agregó a mano, que no están en la lista
 * maestra. Van después de las conocidas y en el orden en que las cargó: son
 * pocas y suyas, así que el orden que eligió es el que tiene sentido.
 *
 * Existen porque la lista maestra no puede estar completa —hay cientos de obras
 * sociales sindicales y regionales— y un consultorio que atiende una que no
 * está no tenía forma de ofrecerla. El id se genera del nombre con
 * `idDeObraSocial`, así que conserva la propiedad que justifica la lista
 * maestra: dos formas de escribir lo mismo caen en el mismo id.
 */
export const obrasSocialesDelNegocio = (ids = [], propias = []) => {
  const deLaLista = obrasSociales.filter((o) => ids.includes(o.id));

  const conocidas = new Set(obrasSociales.map((o) => o.id));
  const agregadas = (Array.isArray(propias) ? propias : [])
    .filter((o) => o && o.id && o.nombre && !conocidas.has(o.id) && ids.includes(o.id));

  return [...deLaLista, ...agregadas];
};
